import {
  Zap,
  GitPullRequest,
  GitMerge,
  Users,
  Clock,
  TrendingUp,
  Activity,
  AlertTriangle,
  BarChart3,
  Radio,
  History,
  SquarePen,
  Table2,
  PieChart,
  Sparkles,
  ScatterChart,
} from 'lucide-react';

export type SlashCommandAction =
  | { type: 'send'; prompt: string }
  | { type: 'ui'; action: 'new_chat' | 'open_history' }
  | { type: 'format'; suffix: string; label: string };

export type SlashCommand = {
  id: string;
  label: string;
  description: string;
  icon: any;
  tier: 1 | 2 | 3;
  action: SlashCommandAction;
};

export const SLASH_COMMANDS: SlashCommand[] = [
  // ── Tier 1 — quick-fire essentials ──────────────────────────────────────
  {
    id: 'briefing',
    label: '/briefing',
    description: 'Morning engineering briefing',
    icon: Radio,
    tier: 1,
    action: {
      type: 'send',
      prompt:
        'Give me a morning engineering briefing — check for any alerts or anomalies, summarise open pull requests, and give me an org health overview.',
    },
  },
  {
    id: 'dora',
    label: '/dora',
    description: 'DORA metrics summary',
    icon: BarChart3,
    tier: 1,
    action: {
      type: 'send',
      prompt: 'Give me a DORA metrics summary for this org. Present it as a vanti-summary card.',
    },
  },
  {
    id: 'timeline',
    label: '/timeline',
    description: 'Project timeline from the beginning',
    icon: Activity,
    tier: 1,
    action: {
      type: 'send',
      prompt:
        'Build me a timeline of this project from the beginning. Show activity by month as a vanti-table with columns: Period, Commits, PRs Merged, Contributors.',
    },
  },
  {
    id: 'new',
    label: '/new',
    description: 'Start a new conversation',
    icon: SquarePen,
    tier: 1,
    action: { type: 'ui', action: 'new_chat' },
  },
  {
    id: 'history',
    label: '/history',
    description: 'Open conversation history',
    icon: History,
    tier: 1,
    action: { type: 'ui', action: 'open_history' },
  },

  // ── Tier 2 — analytics quick-fire ───────────────────────────────────────
  {
    id: 'velocity',
    label: '/velocity',
    description: 'Commit velocity last 30 days',
    icon: TrendingUp,
    tier: 2,
    action: {
      type: 'send',
      prompt:
        'Show me commit velocity for the last 30 days. Plot it as a line chart and highlight any significant peaks or drops.',
    },
  },
  {
    id: 'sprint',
    label: '/sprint',
    description: 'This sprint vs last sprint',
    icon: Zap,
    tier: 2,
    action: {
      type: 'send',
      prompt:
        'How did we perform this sprint vs last sprint? Compare commits, PRs merged, and cycle time. Present as a vanti-summary with sentiment.',
    },
  },
  {
    id: 'prs',
    label: '/prs',
    description: 'Open pull requests and their age',
    icon: GitPullRequest,
    tier: 2,
    action: {
      type: 'send',
      prompt:
        'Show me all open pull requests and how long they have been open. Highlight any that are older than 5 days.',
    },
  },
  {
    id: 'merges',
    label: '/merges',
    description: 'PRs merged this week',
    icon: GitMerge,
    tier: 2,
    action: {
      type: 'send',
      prompt: 'What pull requests were merged this week? Show as a vanti-table with author and title.',
    },
  },
  {
    id: 'bottlenecks',
    label: '/bottlenecks',
    description: 'PR review bottlenecks',
    icon: AlertTriangle,
    tier: 2,
    action: {
      type: 'send',
      prompt:
        'Where are the bottlenecks in our pull request review process? Look at time-to-merge, PRs waiting for review, and reviewer load.',
    },
  },
  {
    id: 'stale',
    label: '/stale',
    description: 'Stale PRs and branches',
    icon: Clock,
    tier: 2,
    action: {
      type: 'send',
      prompt:
        'Show me stale pull requests and branches that have not had any activity in the last 14 days. List them as a vanti-table.',
    },
  },
  {
    id: 'contributors',
    label: '/contributors',
    description: 'Most active contributors this month',
    icon: Users,
    tier: 2,
    action: {
      type: 'send',
      prompt:
        'Who are the most active contributors this month? Show commits, PRs opened, and PRs merged as a vanti-table sorted by activity.',
    },
  },
  {
    id: 'workload',
    label: '/workload',
    description: 'Team workload distribution',
    icon: PieChart,
    tier: 2,
    action: {
      type: 'send',
      prompt:
        'Show me workload distribution across the team for the last 30 days. Use a donut chart for commit share and a vanti-table for per-person breakdown.',
    },
  },
  {
    id: 'leaderboard',
    label: '/leaderboard',
    description: 'Contributor leaderboard',
    icon: Sparkles,
    tier: 2,
    action: {
      type: 'send',
      prompt:
        'Give me a contributor leaderboard for this month ranked by total commits and PRs merged. Present as a vanti-table.',
    },
  },

  // ── Tier 3 — output format overrides ────────────────────────────────────
  {
    id: 'table',
    label: '/table',
    description: 'Format answer as a table',
    icon: Table2,
    tier: 3,
    action: {
      type: 'format',
      suffix: 'Present the answer as a vanti-table.',
      label: 'Table format',
    },
  },
  {
    id: 'chart',
    label: '/chart',
    description: 'Format answer as a chart',
    icon: BarChart3,
    tier: 3,
    action: {
      type: 'format',
      suffix: 'Present the answer as a chart (bar or line).',
      label: 'Chart format',
    },
  },
  {
    id: 'summary',
    label: '/summary',
    description: 'Format answer as a summary card',
    icon: Sparkles,
    tier: 3,
    action: {
      type: 'format',
      suffix: 'Present the answer as a vanti-summary card with headline, bullets, and sentiment.',
      label: 'Summary card',
    },
  },
  {
    id: 'scatter',
    label: '/scatter',
    description: 'Format answer as a scatter plot',
    icon: ScatterChart,
    tier: 3,
    action: {
      type: 'format',
      suffix: 'Present the answer as a Vega-Lite scatter plot.',
      label: 'Scatter plot',
    },
  },
  {
    id: 'heatmap',
    label: '/heatmap',
    description: 'Format answer as a heatmap',
    icon: ScatterChart,
    tier: 3,
    action: {
      type: 'format',
      suffix: 'Present the answer as a Vega-Lite heatmap.',
      label: 'Heatmap',
    },
  },
];

export function filterCommands(query: string): SlashCommand[] {
  const q = query.toLowerCase();
  return SLASH_COMMANDS.filter(
    (c) => c.id.includes(q) || c.description.toLowerCase().includes(q),
  );
}
